import { AxiosInstance } from 'axios'
import axiosInstance from '@/api/index'
import {
  Address,
  CiviliteType,
  Email,
  ModeType,
  Page,
  PaymentMethod,
  PaymentTerm,
  PaymentZone,
  Phone,
  RegimeType,
  RequestPage,
} from '@/types'
import { EntityTrail } from '@/utils/entityTrail'
import { CompanyType, CustomerType, ManType } from '@/utils/customerType'
import { SellRoleType } from '@/utils/sellRoles'
import { LangueType } from '@/utils/language'
import fileDownload from 'js-file-download'
import { ExternalUserDto, User } from '../user'
import { LocalDateTime } from '../dto'

// TYPES
export interface MinimalAccountingSale {
  id: number
  name: string
  number: string
}

interface DescriptionInformationRoot {
    name: string,
    emails: Email[],
    language: LangueType,
    inactive: boolean,
    comment?: string,
    myArqanaEntity?: ExternalUserDto
    myArqanaEntityId?: number
    external?: boolean
}

export interface DescriptionManInformation extends DescriptionInformationRoot {
    civilite: CiviliteType | ''
    civiliteLettre?: string
    firstName: string
    deces: boolean
}

export interface DescriptionCompanyInformation extends DescriptionInformationRoot {
    businessReason: string
    prefixHaras?: string
    type: CompanyType
}

export interface CoordinatesInformation {
    phones: Phone[],
    addresses: Address[],
}

export interface BankingInformation {
    paymentZone: PaymentZone,
    paymentTerm: PaymentTerm,
    paymentMethod: PaymentMethod,
    accountOwner?: string,
    accountNumber?: string,
    iban?: string,
    bic?: string,
    bankIdentifier?: string,
    bankAddress?: string,
    purpose?: string,
    galopFrAccount?: string,
    acceptedSuggestion?: boolean
}

export interface BankingInformationSuggestion {
    paymentZoneSuggestion: PaymentZone,
    paymentTermSuggestion: PaymentTerm,
    paymentMethodSuggestion: PaymentMethod,
    accountOwnerSuggestion?: string,
    accountNumberSuggestion?: string,
    ibanSuggestion?: string,
    bicSuggestion?: string,
    bankIdentifierSuggestion?: string,
    bankAddressSuggestion?: string,
    purposeSuggestion?: string,
    galopFrAccountSuggestion?: string,
}

export interface AccountantInformation {
    tvaId?: string,
    tvaIdUpdatedTime?: LocalDateTime,
    tvaIdUpdater?: User,
    regime?: RegimeType,
    tvaRegimeUpdatedTime?: LocalDateTime,
    tvaRegimeUpdater?: User,
    withoutVat?: boolean,
    mode?: ModeType,
    codeA?: string,
    codeV?: string,
    commentAccountant?: string,
    frfAvailable?: boolean
    flatRateFarmerNumber?: string,
    acceptedSuggestion?: boolean,
    sirenNumber?: string,
}

export interface AccountantInformationSuggestion {
  withoutVatSuggestion?: boolean,
  vatNumberSuggestion?: string,
  frfNumberSuggestion?: string,
}

export interface AdditionalInformation {
    fonctions: string[],
    classifications: string[],
    slips: string[],
    catalogues: string[],
    invitations: string[],
}

// REQUESTS

interface CustomerRequestRoot extends CoordinatesInformation {
    bankingInformation?: BankingInformation
    bankingInformationSuggestion?: BankingInformationSuggestion
    accountantInformation?: AccountantInformation
    accountantInformationSuggestion?: AccountantInformationSuggestion
}

// MAN

export interface ManRequest extends CustomerRequestRoot, DescriptionManInformation, AdditionalInformation {
    id: number, // TODO à déplacer plus tard
}

// COMPANY

export interface MemberShipInformationRequest {
    customerId: number
    function: string
}

export interface CompanyRequest extends CustomerRequestRoot, DescriptionCompanyInformation {
    members: MemberShipInformationRequest[]
}

export type CustomerRequest = ManRequest | CompanyRequest

// MINIMALS

interface CustomerCore {
    id: number
    type: CustomerType
    name: string
    hasHeaders: boolean
    linkedTo: boolean
}

export interface MinimalMan extends CustomerCore {
    type: ManType
    firstName: string
    deces: boolean
}

export interface MinimalCompany extends CustomerCore {
    type: CompanyType
    businessReason: string
    prefixHaras?: string
}

export type MinimalCustomer = MinimalMan | MinimalCompany

// MINIMAL DTOS

interface MinimalCustomerRoot {
    inactive: boolean
    emails: Email[]
    phones: Phone[]
    codeV?: string
    codeA?: string
}

export interface MinimalManDto extends MinimalCustomerRoot, MinimalMan {
    civilite: string
    civiliteLettre: string
}

export interface MinimalCompanyDto extends MinimalCustomerRoot, MinimalCompany {
}

export type MinimalCustomerDto = MinimalManDto | MinimalCompanyDto

export interface SoldeBySale {
  accountingSale: MinimalAccountingSale
  soldeSeller: number
  compensation: number
  soldeToUse?: number
}

export interface CompensationCustomerDto {
    type: CustomerType
    id: number
    name: string
    firstName?: string
    businessReason?: string
    codeV?: string
    codeA?: string
    addresses: Address[]
    accountantInformation?: AccountantInformation
    soldeSeller?: number
    detailedSoldeSeller?: SoldeBySale[]
    soldeBuyer?: number
    inactive: boolean
}

// OBJECTS

interface CustomerRoot extends EntityTrail {
    oldIds: string
    hasMyArqanaAccount: boolean
    hasValidatedComplianceRecord: boolean
    hasMyArqanaUpdate?: boolean
}

export interface MembershipDto {
    customer: MinimalCustomerDto
    function: string
    createMode: boolean
    civilite?: string
    name?: string
    firstName?: string
}

export interface Man extends CustomerRoot, MinimalMan, ManRequest {
    lastBoughtDate?: string
    lastSoldDate?: string
    members: MembershipDto[]
}

export interface Company extends CustomerRoot, MinimalCompany, CustomerRequestRoot, DescriptionCompanyInformation {
    members: MembershipDto[]
}

export type Customer = Man | Company

// FILTERS

export interface CustomerFilters {
    name?: string
    zip?: string
    town?: string
    country?: string
    phone?: string
    email?: string
    code?: string
    fonction?: string
    customerType?: CustomerType
    technicalSaleId?: number
    technicalSaleGroupId?: number
    role?: string
    sellRole?: SellRoleType
    hasCodeV?: boolean
    hasCodeA?: boolean
    vatNumber?: string
    lotExposingYear?: number
    lotExposingMonth?: string[]
    hasMyArqanaUpdate?: boolean
}

export interface MinimalCustomerWithCode {
    id: number
    type: CustomerType
    firstName: string
    name: string
    businessReason: string
    codeV: string
}

export function mapToSuggestion(bankingInfo: BankingInformation | undefined): BankingInformationSuggestion | undefined {
  return bankingInfo ? {
    paymentZoneSuggestion: bankingInfo.paymentZone,
    paymentTermSuggestion: bankingInfo.paymentTerm,
    paymentMethodSuggestion: bankingInfo.paymentMethod,
    accountOwnerSuggestion: bankingInfo.accountOwner,
    accountNumberSuggestion: bankingInfo.accountNumber,
    ibanSuggestion: bankingInfo.iban,
    bicSuggestion: bankingInfo.bic,
    bankIdentifierSuggestion: bankingInfo.bankIdentifier,
    bankAddressSuggestion: bankingInfo.bankAddress,
    purposeSuggestion: bankingInfo.purpose,
    galopFrAccountSuggestion: bankingInfo.galopFrAccount,
  } as BankingInformationSuggestion : undefined
}

export function formatConsignerName(listSeller: string | undefined, consigner: Customer | undefined): string {
  if (listSeller) {
    return listSeller
  } else if (consigner) {
    return consigner.name
  } else {
    return ''
  }
}

class CustomerApi {
    private axios: AxiosInstance

    public constructor(axios: AxiosInstance) {
      this.axios = axios
    }

    public async getAllCustomer(page: RequestPage, filters: CustomerFilters): Promise<Page<Customer>> {
      const axiosResponse = await this.axios.get('/customer', { params: { ...page, ...filters } })
      return axiosResponse.data
    }

    public async getAllCustomerWithAssociates(page: RequestPage, filters: CustomerFilters): Promise<Customer[]> {
      const axiosResponse = await this.axios.get('/customer/associates', { params: { ...page, ...filters } })
      return axiosResponse.data
    }

    public async getAllCompensationCustomer(page: RequestPage, filters: CustomerFilters): Promise<Page<CompensationCustomerDto>> {
      const axiosResponse = await this.axios.get('/customer/compensations', { params: { ...page, ...filters } })
      return axiosResponse.data
    }

    public async getAllMen(page: RequestPage, filters: CustomerFilters): Promise<Page<Man>> {
      const axiosResponse = await this.axios.get('/customer', { params: { ...page, ...filters, customerType: 'MAN' } })
      return axiosResponse.data
    }

    public async getAllAssociates(id: number): Promise<MembershipDto[]> {
      const axiosResponse = await this.axios.get(`/customer/${id}/associates`)
      return axiosResponse.data
    }

    public async findById(id: number): Promise<Customer> {
      return (await this.axios.get(`/customer/${id}`)).data
    }

    public async findSimpleCustomerById(id: number): Promise<MinimalCustomerDto> {
      return (await this.axios.get('/customer', { params: { simpleCustomerId: id } })).data
    }

    public async deleteCustomer(id: number): Promise<void> {
      await this.axios.delete(`/customer/${id}`)
    }

    public async generateMandat(customerId: number, groupSaleId: number | undefined) {
      const response = await this.axios.get(`/customer/${customerId}/mandat/${groupSaleId}`, { params: { }, responseType: 'blob' })
      fileDownload(response.data, 'mandat.pdf')
    }

    public async getUpdatedCustomerCount() {
      return (await this.axios.get('/customer/updated-count')).data
    }
}

export const customerApi = new CustomerApi(axiosInstance)
